<template>
  <TopBar :logo-url="logoUrl">
    <div class="signing-page-top-bar-content">
      <template v-if="showInviteOption">
        <FriedButton
          data-external="signing-page-topbar-recipients-button"
          class="hidden-xs-and-down"
          :button-type="ButtonType.Secondary"
          :disabled="isPreview"
          @click="$emit('invite')"
        >
          <template #leftIcon>
            <FriedIcon icon="user-add"></FriedIcon>
          </template>
          {{ $t('invite') }}
        </FriedButton>
        <FriedIconButton
          data-external="signing-page-topbar-recipients-button"
          class="hidden-xs-and-up"
          :disabled="isPreview"
          @click="$emit('invite')"
        >
          <FriedIcon icon="user-add"></FriedIcon>
        </FriedIconButton>
      </template>
      <CommentHistoryDropdown
        v-if="commentHistoryVisible"
        data-external="signing-page-topbar-comment-history-button"
        :disabled="isPreview"
        :recipient="recipient"
        :recipients="recipients"
        :sender="sender"
        :prevent-comments-modification="preventCommentsModification"
        @go-to-comment="handleGoToComment"
      />
      <ChatButton
        v-if="showChatOption"
        class="hidden-xs-and-up"
        :new-comments-count="newCommentsCount"
        @click="$emit('chat')"
      />
      <FriedDropdown
        :main-axis-offset="8"
        :cross-axis-offset="8"
        :placement="Placement.BottomEnd"
        :close-on-action="closeOnAction"
      >
        <template #trigger>
          <FriedIconButton data-external="signing-page-top-bar-menu-button">
            <FriedIcon icon="hamburger-menu" />
          </FriedIconButton>
        </template>
        <template #content>
          <div class="dropdown-content">
            <template v-if="showContextualComments">
              <FriedSwitch
                data-external="signing-page-topbar-hide-comments-menu-item"
                :model-value="contextualCommentingStore.commentsHidden"
                :label="$t('cc_hide_all_comments_markups')"
                @update:modelValue="toggleComments"
              />
              <hr />
            </template>
            <div @click="closeOnAction = true">
              <FriedDropdownItem
                v-if="showInviteOption"
                v-tooltip="isPreview ? $t('disabled-in-preview-mode') : null"
                data-external="signing-page-topbar-invite-menu-item"
                :disabled="isPreview"
                @click="$emit('invite')"
              >
                <template #leftIcon>
                  <FriedIcon icon="user-add" />
                </template>
                {{ $t('invite') }}
              </FriedDropdownItem>
              <FriedDropdownItem
                v-tooltip="isPreview ? $t('disabled-in-preview-mode') : null"
                :disabled="isPreview"
                data-external="signing-page-topbar-recipients-menu-item"
                @click="$emit('recipients')"
              >
                <template #leftIcon>
                  <FriedIcon icon="users" />
                </template>
                <div class="recipients-count-container">
                  {{ $t('recipients') }}
                  <div class="count-bubble">
                    <FriedH5>{{ recipients.length }}</FriedH5>
                  </div>
                </div>
              </FriedDropdownItem>
              <FriedDropdownItem
                v-if="showCallOption"
                v-tooltip="isPreview ? $t('disabled-in-preview-mode') : null"
                data-external="signing-page-topbar-call-menu-item"
                :disabled="isPreview"
                @click="$emit('call')"
              >
                <template #leftIcon>
                  <FriedIcon icon="phone-call" />
                </template>
                {{ $t('call') }}
              </FriedDropdownItem>
              <FriedDropdownItem
                v-if="showDownloadOption"
                v-tooltip="isPreview ? $t('disabled-in-preview-mode') : null"
                data-external="signing-page-topbar-download-menu-item"
                :disabled="isPreview"
                @click="$emit('download')"
              >
                <template #leftIcon>
                  <FriedIcon icon="download" />
                </template>
                {{ $t('download') }}
              </FriedDropdownItem>
              <FriedDropdownItem
                v-if="showPrintOption"
                v-tooltip="isPreview ? $t('disabled-in-preview-mode') : null"
                data-external="signing-page-topbar-print-menu-item"
                :disabled="isPreview"
                @click="$emit('print')"
              >
                <template #leftIcon>
                  <FriedIcon icon="print" />
                </template>
                {{ $t('print') }}
              </FriedDropdownItem>
              <FriedDropdownItem
                v-if="showRejectDocumentOption"
                data-external="signing-page-topbar-reject-menu-item"
                :disabled="isPreview"
                @click="$emit('reject')"
              >
                <template #leftIcon>
                  <FriedIcon icon="close-circle" />
                </template>
                {{ $t('reject_document') }}
              </FriedDropdownItem>
              <hr />
              <FriedLink
                class="help-link"
                data-external="signing-page-topbar-help-menu-item"
                href="https://help.getaccept.com/en/collections/84327-getaccept-help-guide-for-the-recipients-of-a-document"
                target="_blank"
              >
                {{ $t('top_bar_document_signing_help_link_text') }}
              </FriedLink>
            </div>
          </div>
        </template>
      </FriedDropdown>
    </div>
  </TopBar>
</template>

<script lang="ts">
import { useScrollToComment } from '@getaccept/lib-shared-new/src/contextual-commenting/composables/scroll-to-comment.composable';
import { useContextualCommentingStore } from '@getaccept/lib-shared-new/src/contextual-commenting/store/contextual-commenting.store';
import type { Recipient } from '@getaccept/lib-shared-new/src/types/Recipient';
import type { SigningPageRecipient } from '@getaccept/lib-shared-new/src/types/signing-page-recipient';
import type { User } from '@getaccept/lib-shared-new/src/users/types/user';
import { ButtonType, Placement } from '@getaccept/fried-tofu';
import type { PropType } from 'vue';
import { computed, defineComponent, ref } from 'vue';
import TopBar from '@getaccept/lib-shared-new/src/top-bar/components/TopBar.vue';
import CommentHistoryDropdown from '../../contextual-commenting/components/CommentHistoryDropdown.vue';
import ChatButton from './ChatButton.vue';

export default defineComponent({
  components: {
    TopBar,
    CommentHistoryDropdown,
    ChatButton,
  },
  props: {
    logoUrl: {
      type: String,
      default: '',
    },
    recipients: {
      type: Array as PropType<Recipient[]>,
      default: null,
    },
    sender: {
      type: Object as PropType<User>,
      default: null,
    },
    recipient: {
      type: Object as PropType<SigningPageRecipient>,
      default: null,
    },
    newCommentsCount: {
      type: Number,
      default: 0,
    },
    showContextualComments: {
      type: Boolean,
      default: false,
    },
    showDownloadOption: {
      type: Boolean,
      default: false,
    },
    showPrintOption: {
      type: Boolean,
      default: false,
    },
    showCallOption: {
      type: Boolean,
      default: false,
    },
    isPreview: {
      type: Boolean,
      default: false,
    },
    showChatOption: {
      type: Boolean,
      default: false,
    },
    showInviteOption: {
      type: Boolean,
      default: false,
    },
    showRejectDocumentOption: {
      type: Boolean,
      default: false,
    },
    preventCommentsModification: {
      type: Boolean,
    },
  },
  setup(props) {
    const closeOnAction = ref(true);
    const contextualCommentingStore = useContextualCommentingStore();

    const commentHistoryVisible = computed(
      () => props.showContextualComments && props.recipients && props.recipient && props.sender
    );

    const toggleComments = (value: boolean) => {
      closeOnAction.value = false;
      contextualCommentingStore.commentsHidden = value;
    };

    const { scrollToComment } = useScrollToComment();

    const handleGoToComment = (commentId: string) => {
      scrollToComment(commentId, contextualCommentingStore.showFloatingComments);
    };

    return {
      ButtonType,
      contextualCommentingStore,
      commentHistoryVisible,
      closeOnAction,
      toggleComments,
      handleGoToComment,
      Placement,
    };
  },
});
</script>

<style lang="scss" scoped>
$recipients-count-bubble-size: 1rem;

.signing-page-top-bar-content {
  display: flex;
  justify-content: right;
  align-items: center;
  width: 100%;
  gap: var(--spacing-150);

  @media screen and (max-width: $xs) {
    gap: var(--spacing-50);
  }

  .dropdown-content {
    min-width: 322px;
    padding: var(--spacing-50);

    .help-link {
      padding-left: var(--spacing-50);
    }

    hr {
      border-top: 1px solid var(--gray-96);
      margin: var(--spacing-100) 0;
    }

    .recipients-count-container {
      display: flex;
      align-items: center;
      gap: var(--spacing-50);

      .count-bubble {
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: var(--gray-94);
        height: $recipients-count-bubble-size;
        width: $recipients-count-bubble-size;
        border-radius: 50%;
      }
    }
  }
}
</style>
